import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import { EmphasisTag } from '@wtag/react-comp-lib';
import CheckBox from '@wtag/rcl-check-box';
import Tooltip from '@wtag/rcl-tooltip';
import JourneySummaryIndicator from 'sharedWebpack/JourneySummaryIndicator';
import DatePickerWithFocusedStateWrapper from 'sharedWebpack/DatePickerWithFocusedStateWrapper';
import showInvalidDateError from '../../../../../helpers/showInvalidDateError';
import getMinDate from '../../../helpers/getMinDate';
import { searchAirports } from '../../../../../actions/flight';
import SearchSelectOption from '../../../AdvancedFlightForm/AdvancedFlightFormTrips/SearchSelectOption';
import FlightRoute from '../../FlightRoute';
import { ONEWAY } from '../../../../../lib/helpers/tripTypes';
import formTripShape from '../../../helpers/shapes';
import { setTripSelectionStatus } from '../../../../../actions/common/exchange';
import './styles.scss';

const ongoingFetches = new Set();

const ExchangeFormTrip = ({
  isAirportLoading,
  fetchAirport,
  airports,
  index,
  trip,
  searchInputConfiguration,
  onTripSelect,
  tripSelectionStatus,
  canPartialExchange,
  handleCabinClassChange,
  canChangeBookingClass,
  canChangeCabinClass,
  agentDateFormat,
}) => {
  const [hasTripChanged, setHasTripChanged] = useState(false);
  const { origin, destination, date, cabinClass, bookingClass } = trip;
  const originCode = origin.value;
  const destinationCode = destination.value;
  const { whitelistedAirports, showBookingClass } = searchInputConfiguration;
  const originInfo = airports[originCode];
  const destinationInfo = airports[destinationCode];

  const onDateChange = departureDate => {
    if (!departureDate) {
      date.onChange('');
    } else {
      date.onChange(moment(departureDate).format('YYYY-MM-DD'));
    }
  };

  const fetchAirportData = airportCode => {
    if (airportCode && !airports[airportCode] && !ongoingFetches.has(airportCode)) {
      ongoingFetches.add(airportCode);
      fetchAirport(airportCode, () => ongoingFetches.delete(airportCode), whitelistedAirports);
    }
  };

  const resetFormData = () => {
    date.onChange(date.initialValue);
    handleCabinClassChange(cabinClass.initialValue);
    bookingClass.onChange(bookingClass.initialValue);
  };

  const toggleTripSelection = event => {
    if (!event.target.checked) {
      resetFormData();
    }

    onTripSelect({
      ...tripSelectionStatus,
      [`${originCode}-${destinationCode}`]: event.target.checked,
    });
  };

  const partialExchangeAttempted = Object.values(tripSelectionStatus).some(value => !value);

  useEffect(() => {
    [originCode, destinationCode].forEach(fetchAirportData);
  }, [originCode, destinationCode]);

  useEffect(() => {
    setHasTripChanged(date.dirty || cabinClass.dirty || bookingClass.dirty);
  }, [date.dirty, cabinClass.dirty, bookingClass.dirty]);

  return (
    <div className="exchange-form-trip d-flex align-center justify-between flex-wrap row-gap-20">
      <div className="d-flex align-center column-gap-20 flex-grow">
        <CheckBox
          className={classNames({
            'exchange-form-trip__checkbox--disabled': !canPartialExchange,
          })}
          name={`${originCode}-${destinationCode}`}
          size="small"
          isChecked={tripSelectionStatus[`${originCode}-${destinationCode}`]}
          onChange={toggleTripSelection}
        />
        <div className="exchange-form-trip__indicator d-flex-column row-gap-4">
          <div className="d-flex align-center column-gap-8">
            <JourneySummaryIndicator
              originCountryInfo={originInfo}
              destinationCountryInfo={destinationInfo}
              tripType={ONEWAY}
              isLoading={isAirportLoading}
              isTripTypeVisible={false}
            />
            {hasTripChanged && (
              <EmphasisTag
                text={I18n.t('components.flight.exchange.tag.change')}
                type="danger"
                size="tiny"
              />
            )}
          </div>
          <FlightRoute originInfo={originInfo} destinationInfo={destinationInfo} />
        </div>
      </div>
      <div
        className={classNames('d-flex align-center flex-gap-20 flex-wrap', {
          'exchange-form-trip__field--disabled': !tripSelectionStatus[
            `${originCode}-${destinationCode}`
          ],
        })}
      >
        <DatePickerWithFocusedStateWrapper
          pastYearsCount={0}
          futureYearsCount={1}
          size="small"
          placeholder={I18n.t('components.ibe.search_form.flight.label.departure')}
          onChange={onDateChange}
          fullWidth={true}
          showClearDate={true}
          minDate={getMinDate(trip, index)}
          hideMonthsAndYearsWithNoActiveDates={true}
          date={date.value}
          locale={moment().utc()}
          initialDate={date.value}
          id={Math.random()}
          onFocusChange={date.onFocus}
          touched={date.touched}
          error={showInvalidDateError(date)}
          hideNavButtons={false}
          displayFormat={agentDateFormat}
        />
        <div
          className={classNames({
            'exchange-form-trip__field--disabled':
              tripSelectionStatus[`${originCode}-${destinationCode}`] && partialExchangeAttempted,
          })}
        >
          {!canChangeCabinClass ? (
            <Tooltip
              type="inverse"
              content={I18n.t('components.flight.exchange.disabled_cabin_class')}
            >
              <SearchSelectOption
                className="exchange-form-trip__cabin-class-select"
                field="cabinClass"
                trip={trip}
                handleCabinClassChange={handleCabinClassChange}
                isDisabled={true}
              />
            </Tooltip>
          ) : (
            <SearchSelectOption
              className="exchange-form-trip__cabin-class-select"
              field="cabinClass"
              trip={trip}
              handleCabinClassChange={handleCabinClassChange}
            />
          )}
        </div>
        {showBookingClass && (
          <SearchSelectOption
            className="exchange-form-trip__booking-class-select"
            field="bookingClass"
            trip={trip}
            isDisabled={!canChangeBookingClass}
          />
        )}
      </div>
    </div>
  );
};

ExchangeFormTrip.defaultProps = {
  airports: {},
  tripSelectionStatus: {},
  canChangeBookingClass: false,
  canChangeCabinClass: false,
};

ExchangeFormTrip.propTypes = {
  airports: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({}),
  }),
  isAirportLoading: PropTypes.bool.isRequired,
  fetchAirport: PropTypes.func.isRequired,
  trip: PropTypes.shape(formTripShape).isRequired,
  index: PropTypes.number.isRequired,
  searchInputConfiguration: PropTypes.shape({
    whitelistedAirports: PropTypes.arrayOf(PropTypes.string).isRequired,
    showBookingClass: PropTypes.bool,
  }).isRequired,
  onTripSelect: PropTypes.func.isRequired,
  tripSelectionStatus: PropTypes.shape({
    [PropTypes.string]: PropTypes.bool,
  }),
  canPartialExchange: PropTypes.bool.isRequired,
  handleCabinClassChange: PropTypes.func.isRequired,
  canChangeBookingClass: PropTypes.bool,
  canChangeCabinClass: PropTypes.bool,
  agentDateFormat: PropTypes.string.isRequired,
};

const mapStateToProps = state => {
  const isAirportLoading = Object.values(state.flights.airportLoading).some(isLoading => isLoading);

  return {
    airports: state.common.airports,
    isAirportLoading,
    tripSelectionStatus: state.common.exchange.tripSelectionStatus,
    agentDateFormat: state.common.agentDateFormat,
  };
};

const mapDispatchToProps = dispatch => ({
  fetchAirport: (query, callback, whitelistedAirports) =>
    dispatch(
      searchAirports(query.toUpperCase(), query.toUpperCase(), callback, whitelistedAirports),
    ),
  onTripSelect: data => dispatch(setTripSelectionStatus(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExchangeFormTrip);
